import { connect } from "react-redux";
import { getSuperAdminSubscriber } from "../../../actions/bookingHistoryAction";
import Loader from "../../../components/Loader/Loader";
import { Search, X } from "react-feather";
import { useEffect, useState, useRef } from "react";
import UserDetailsModal from "./UserDetailsModal";
import "./users-details.scss";

const thStyle = {
  padding: "12px 14px",
  textAlign: "left",
  fontWeight: 600,
  fontSize: "13px",
  color: "#555",
  borderBottom: "2px solid #e8e8e8",
  whiteSpace: "nowrap",
  cursor: "pointer",
  userSelect: "none",
  background: "#f7f9fb",
};

const tdStyle = {
  padding: "11px 14px",
  fontSize: "13px",
  color: "#333",
  verticalAlign: "middle",
};

const btnStyle = (disabled) => ({
  padding: "6px 16px",
  fontSize: "13px",
  borderRadius: "4px",
  border: "1px solid #d5dde5",
  background: disabled ? "#f5f5f5" : "#fff",
  color: disabled ? "#aaa" : "#333",
  cursor: disabled ? "default" : "pointer",
});

const detailBtnStyle = {
  padding: "5px 12px",
  fontSize: "12px",
  borderRadius: "4px",
  border: "1px solid #255a77",
  background: "#fff",
  color: "#255a77",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const SORTABLE_COLUMNS = [
  { key: "id", label: "#" },
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "subscription_type", label: "Plan" },
  { key: "plan_type", label: "Billing" },
  { key: "is_paid", label: "Type" },
  { key: "account_age", label: "Account Age" },
  { key: "last_login_date", label: "Last Login" },
];

function SearchField({ id, label, name, value, placeholder, onChange, onClear }) {
  return (
    <div className="users-details-field">
      <label className="users-details-field__label" htmlFor={id}>
        {label}
      </label>
      <div className="users-details-field__control">
        <span className="users-details-field__icon" aria-hidden="true">
          <Search size={15} />
        </span>
        <input
          id={id}
          type="text"
          name={name}
          value={value}
          className="users-details-field__input"
          onChange={onChange}
          placeholder={placeholder}
          autoComplete="off"
        />
        {value ? (
          <button
            type="button"
            className="users-details-field__clear"
            onClick={() => onClear(name)}
            aria-label={`Clear ${label}`}
          >
            <X size={14} />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function SubcriberDetailsMain() {
  const [loader, setLoader] = useState(false);
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filter, setFilter] = useState({
    pageSize: 10,
    pageNumber: 1,
    sortField: "id",
    sortOrder: "desc",
    filter: { name: "", email: "", user_type: "all" },
  });

  const debounceRef = useRef(null);

  const fetchUsers = async (currentFilter) => {
    setLoader(true);
    try {
      const response = await getSuperAdminSubscriber(currentFilter);
      setUsers(response.data.entity || []);
      setTotalCount(response.data.totalCount || 0);
    } catch (e) {
      setUsers([]);
      setTotalCount(0);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchUsers(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.pageNumber, filter.pageSize, filter.sortField, filter.sortOrder]);

  const applyFilterUpdate = (nextInnerFilter, debounceMs = 0) => {
    const newFilter = {
      ...filter,
      pageNumber: 1,
      filter: nextInnerFilter,
    };
    setFilter(newFilter);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (debounceMs > 0) {
      debounceRef.current = setTimeout(() => fetchUsers(newFilter), debounceMs);
    } else {
      fetchUsers(newFilter);
    }
  };

  const onFilterChange = (e) => {
    const { name, value } = e.target;
    applyFilterUpdate({ ...filter.filter, [name]: value }, name === "user_type" ? 0 : 400);
  };

  const clearField = (name) => {
    applyFilterUpdate({ ...filter.filter, [name]: "" }, 0);
  };

  const clearAllFilters = () => {
    applyFilterUpdate({ name: "", email: "", user_type: "all" }, 0);
  };

  const hasActiveFilters =
    !!filter.filter.name ||
    !!filter.filter.email ||
    (filter.filter.user_type && filter.filter.user_type !== "all");

  const onPageSizeChange = (e) => {
    const pageSize = parseInt(e.target.value, 10) || 10;
    setFilter((f) => ({ ...f, pageSize, pageNumber: 1 }));
  };

  const onSort = (sortField) => {
    setFilter((f) => {
      const sameField = f.sortField === sortField;
      const sortOrder = sameField && f.sortOrder === "asc" ? "desc" : "asc";
      return { ...f, sortField, sortOrder, pageNumber: 1 };
    });
  };

  const sortIndicator = (key) => {
    if (filter.sortField !== key) return " ↕";
    return filter.sortOrder === "asc" ? " ↑" : " ↓";
  };

  const totalPages = Math.ceil(totalCount / filter.pageSize) || 1;
  const startRecord = totalCount === 0 ? 0 : (filter.pageNumber - 1) * filter.pageSize + 1;
  const endRecord = Math.min(filter.pageNumber * filter.pageSize, totalCount);

  const goToPage = (page) => {
    setFilter((f) => ({ ...f, pageNumber: page }));
  };

  return (
    <div className="eventdetailsaddbox rd_noshadow rd_noboxheader users-details-page">
      <Loader isShowLoader={loader} />

      <div className="users-details-page__header">
        <div>
          <h3 className="users-details-page__title">Users Details</h3>
          <p className="users-details-page__subtitle">
            Search and review all MeetOcto account holders
          </p>
        </div>
      </div>

      <div className="users-details-toolbar">
        <div className="users-details-toolbar__top">
          <p className="users-details-toolbar__label">Filters</p>
          <button
            type="button"
            className="users-details-toolbar__clear"
            onClick={clearAllFilters}
            disabled={!hasActiveFilters}
          >
            Clear all
          </button>
        </div>

        <div className="users-details-toolbar__fields">
          <SearchField
            id="users-filter-name"
            label="Name"
            name="name"
            value={filter.filter.name}
            placeholder="Search by name"
            onChange={onFilterChange}
            onClear={clearField}
          />
          <SearchField
            id="users-filter-email"
            label="Email"
            name="email"
            value={filter.filter.email}
            placeholder="Search by email"
            onChange={onFilterChange}
            onClear={clearField}
          />
          <div className="users-details-field">
            <label className="users-details-field__label" htmlFor="users-filter-type">
              Account Type
            </label>
            <div className="users-details-field__control">
              <select
                id="users-filter-type"
                name="user_type"
                className="users-details-field__select"
                value={filter.filter.user_type}
                onChange={onFilterChange}
              >
                <option value="all">All users</option>
                <option value="paid">Paid</option>
                <option value="free">Free</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="users-details-table-wrap">
        <div className="users-details-table-scroll">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {SORTABLE_COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    style={thStyle}
                    onClick={() => onSort(col.key)}
                    title={`Sort by ${col.label}`}
                  >
                    {col.label}
                    <span style={{ color: "#999", fontWeight: 400 }}>{sortIndicator(col.key)}</span>
                  </th>
                ))}
                <th style={{ ...thStyle, cursor: "default" }}>More Details</th>
              </tr>
            </thead>
            <tbody>
              {!loader && users.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    style={{ textAlign: "center", padding: "40px 24px", color: "#8a97a3", fontSize: "14px" }}
                  >
                    {hasActiveFilters
                      ? "No users match your filters. Try adjusting or clearing them."
                      : "No users found"}
                  </td>
                </tr>
              ) : (
                users.map((user, index) => (
                  <tr
                    key={user.id}
                    style={{
                      borderBottom: "1px solid #f0f0f0",
                      background: index % 2 === 0 ? "#fff" : "#fafbfc",
                    }}
                  >
                    <td style={tdStyle}>
                      {(filter.pageNumber - 1) * filter.pageSize + index + 1}
                    </td>
                    <td style={tdStyle}>
                      {user.first_name} {user.last_name}
                    </td>
                    <td style={tdStyle}>{user.email}</td>
                    <td style={tdStyle}>{user.subscription_type}</td>
                    <td style={{ ...tdStyle, textTransform: "capitalize" }}>
                      {user.plan_type !== "-" ? user.plan_type : "-"}
                    </td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          padding: "3px 10px",
                          borderRadius: "12px",
                          fontSize: "11px",
                          fontWeight: 600,
                          background: user.is_paid ? "#e8f5e9" : "#fff3e0",
                          color: user.is_paid ? "#2e7d32" : "#e65100",
                        }}
                      >
                        {user.is_paid ? "Paid" : "Free"}
                      </span>
                    </td>
                    <td style={tdStyle}>{user.account_age}</td>
                    <td style={tdStyle}>{user.last_login_date}</td>
                    <td style={tdStyle}>
                      <button
                        type="button"
                        style={detailBtnStyle}
                        onClick={() => setSelectedUser(user)}
                      >
                        More Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="users-details-pagination">
          <span className="users-details-pagination__meta">
            {totalCount === 0
              ? "No records"
              : `Showing ${startRecord}–${endRecord} of ${totalCount} users`}
          </span>
          <div className="users-details-pagination__controls">
            <label
              htmlFor="users-page-size"
              style={{ fontSize: "13px", color: "#555", margin: 0 }}
            >
              Rows
            </label>
            <select
              id="users-page-size"
              value={filter.pageSize}
              onChange={onPageSizeChange}
              style={{
                padding: "6px 10px",
                fontSize: "13px",
                borderRadius: "4px",
                border: "1px solid #d5dde5",
                background: "#fff",
                color: "#333",
                minWidth: 72,
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
            <button
              onClick={() => goToPage(filter.pageNumber - 1)}
              disabled={filter.pageNumber === 1}
              style={btnStyle(filter.pageNumber === 1)}
            >
              Previous
            </button>
            <span style={{ fontSize: "13px", color: "#555" }}>
              Page {filter.pageNumber} of {totalPages}
            </span>
            <button
              onClick={() => goToPage(filter.pageNumber + 1)}
              disabled={filter.pageNumber >= totalPages}
              style={btnStyle(filter.pageNumber >= totalPages)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <UserDetailsModal
        isOpen={!!selectedUser}
        user={selectedUser}
        onClose={() => setSelectedUser(null)}
      />
    </div>
  );
}

const mapStateToProps = () => ({});
const actionMethods = {};

export default connect(mapStateToProps, actionMethods)(SubcriberDetailsMain);
